export const COMPANY_TECH_SPLITTER = ','

// camelCase working shape from companyFromServer (≠ raw snake_case @albacross/types Company); nested objects are Partial because *FromServer returns {} when the server omits them.
export type CompanyPhoneNumber = { country: string; countryCode: string | number; number: string }
export type CompanyAddress = {
  address: string
  city: string
  zipCode: string
  state: string
  country: string
  type: string
  phoneNumber: Partial<CompanyPhoneNumber>
}
export type CompanyEmployees = { year: number; from: number; to: number | null }
export type CompanyFinancialReport = {
  year: number | null
  currency: string | null
  revenue: number | null
  revenueFrom: number | null
  revenueTo: number | null
}
export type CompanySocials = { linkedIn: string; twitter: string; crunchbase: string; facebook: string }
export type CompanyData = {
  uuid: string
  name: string
  alias: string
  country: string
  number: string
  description: string
  address: Partial<CompanyAddress>
  phoneNumber?: Partial<CompanyPhoneNumber>
  employees: Partial<CompanyEmployees>
  financialReport: Partial<CompanyFinancialReport>
  socials: Partial<CompanySocials>
  foundedAt: number
  naceCode: string
  linkedInIndustryCode: string
  website: string
  totalFunding: number
  tech: string
  isIsp: boolean
}
export type CompanyIpRange = { from: string | number; to: string | number }
export type CompanyDetail = { company: CompanyData; ipRanges: CompanyIpRange[] }

// Empty company shape (was Immutable CompanyDataRecord's default): fallback for an absent company and seed for the bulk change-request modal.
export const emptyCompanyData = (): CompanyData => ({
  uuid: '',
  name: '',
  alias: '',
  country: '',
  number: '',
  description: '',
  address: {
    address: '',
    city: '',
    zipCode: '',
    state: '',
    country: '',
    type: '',
    phoneNumber: { country: '', countryCode: '', number: '' },
  },
  phoneNumber: { country: '', countryCode: '', number: '' },
  employees: { year: 0, from: 0, to: null },
  financialReport: { year: null, currency: null, revenueFrom: null, revenueTo: null, revenue: null },
  socials: { linkedIn: '', twitter: '', crunchbase: '', facebook: '' },
  foundedAt: 0,
  naceCode: '',
  linkedInIndustryCode: '',
  website: '',
  totalFunding: 0,
  tech: '',
  isIsp: false,
})

// Raw snake_case API company → the shared camelCase working shape (companyDataToServer reverses it; a genuine read/write asymmetry, not pure casing).
export const companyDataFromServer = (x): CompanyData =>
  x
    ? {
        uuid: x.uuid,
        name: x.name,
        alias: x.alias,
        country: x.country,
        number: x.number,
        description: x.description,
        address: addressFromServer({ ...x.address, country: x.country }),
        employees: x.employees || {},
        financialReport: financialReportFromServer(x.financial_report),
        socials: socialsFromServer(x.socials),
        foundedAt: x.founded_year,
        naceCode: x.nace_code?.code,
        linkedInIndustryCode: x.linkedin_industry_code?.code,
        website: x.url,
        totalFunding: x.total_funding,
        tech: x.tech?.join(COMPANY_TECH_SPLITTER),
        isIsp: x.is_isp || x.internet_provider || false,
      }
    : emptyCompanyData()

const addressFromServer = (x): Partial<CompanyAddress> =>
  x
    ? {
        address: x.address,
        city: x.city,
        zipCode: x.zip_code,
        state: x.state,
        country: x.country,
        type: x.type,
        phoneNumber: phoneFromServer(x.phone_number),
      }
    : {}

const phoneFromServer = (x): Partial<CompanyPhoneNumber> =>
  x
    ? {
        country: x.country,
        countryCode: x.country_code,
        number: x.number,
      }
    : {}

const socialsFromServer = (x): Partial<CompanySocials> =>
  x
    ? {
        facebook: x.facebook,
        twitter: x.twitter,
        linkedIn: x.linkedin,
        crunchbase: x.crunchbase,
      }
    : {}

const financialReportFromServer = (x): Partial<CompanyFinancialReport> =>
  x
    ? {
        year: x.year,
        currency: x.currency,
        revenue: x.revenue,
        revenueFrom: x.revenue_from,
        revenueTo: x.revenue_to,
      }
    : {}

const companyIpRangeFromServer = (record): CompanyIpRange => {
  const x = record?.value
  return x ? { from: x.gte, to: x.lte } : { from: '', to: '' }
}

export const companyFromServer = (x): CompanyDetail =>
  x
    ? {
        company: companyDataFromServer(x),
        ipRanges: (x.ip_ranges || []).map(companyIpRangeFromServer),
      }
    : { company: emptyCompanyData(), ipRanges: [] }

// filters.{naces,industries,countries} are arrays (the AutoComplete filter inputs).
export const getCompaniesSearchBody = ({ filters }) => ({
  query: filters.search,
  wildcards: (filters.wildcards || '')
    .split(',')
    .filter(x => x)
    .map(x => x.trim()),
  locations: filters.countries.map(country => ({ country })),
  linkedin_industry_codes: filters.industries.reduce((acc, nace) => acc.concat(nace.replace(/\./, '').split(';')), []),
  naces: filters.naces.reduce((acc, nace) => acc.concat(nace.replace(/\./, '').split(';')), []),
  revenue: {
    from: parseInt(filters.revenue_min, 10),
    to: parseInt(filters.revenue_max, 10),
  },
  employees: {
    from: parseInt(filters.employees_min, 10),
    to: parseInt(filters.employees_max, 10),
  },
  url: filters.url || undefined,
})

export const getCompaniesFilename = filters => {
  const countries = filters.countries.slice(0, 5).join('-')
  const naces = filters.naces.join('-') ? `naces-${filters.naces.slice(0, 5).join('-')}` : ''

  const filename = ['companies', countries, naces].filter(x => x).join('--')

  return `${filename}.csv`
}

// Clearing a numeric input yields '', and `x.y && parseInt(x.y)` short-circuits to '' rather than dropping it.
const toInt = value => {
  const parsed = Number.parseInt(value, 10)
  return Number.isNaN(parsed) ? undefined : parsed
}

export const companyDataToServer = x => ({
  uuid: x.uuid,
  name: x.name,
  alias: x.alias,
  country: x.country,
  number: x.number || undefined,
  description: x.description,
  address: x.address && addressToServer(x.address),
  employees: x.employees && employeesToServer(x.employees),
  financial_report: x.financialReport && financialReportToServer(x.financialReport),
  socials: x.socials && socialsToServer(x.socials),
  founded_year: toInt(x.foundedAt),
  nace_code: x.naceCode ? { code: x.naceCode?.replace('.', '') } : undefined,
  linkedin_industry_code: x.linkedInIndustryCode ? { code: x.linkedInIndustryCode } : undefined,
  url: x.website,
  total_funding: toInt(x.totalFunding),
  tech: x.tech?.split(COMPANY_TECH_SPLITTER),
  internet_provider: x.isIsp || undefined,
})

const addressToServer = x => ({
  address: x.address,
  city: x.city,
  zip_code: x.zipCode,
  state: x.state,
  type: x.type || undefined,
  phone_number: x.phoneNumber && phoneToServer(x.phoneNumber),
})

const phoneToServer = x => ({
  country: x.country,
  country_code: toInt(x.countryCode),
  number: x.number,
})

const financialReportToServer = x => ({
  year: toInt(x.year),
  currency: x.currency,
  revenue: toInt(x.revenue),
})

const socialsToServer = x => ({
  facebook: x.facebook,
  twitter: x.twitter,
  linkedin: x.linkedIn,
  crunchbase: x.crunchbase,
})

const employeesToServer = x => ({
  from: toInt(x.from),
  // to might be null for highest range
  to: x.to === null ? null : toInt(x.to),
  year: toInt(x.year),
})
